// Pure pagination math for the admin Tool logs table, factored out of
// useAdminToolCalls so the page-window derivations are unit-testable without a
// React renderer. The backend is keyset-by-id DESC (forward-only, no total
// count), so we page forward by cursor and keep loaded pages in memory; Prev is
// just a backward index step over that buffer.

// One page == 50 rows. Page 0 comes from the cached query; deeper pages are
// fetched on demand via ?before=<lastId> and cached client-side.
export const PAGE_LIMIT = 50;

export type PageWindow = {
  canPrev: boolean;
  canNext: boolean;
  // 1-based inclusive range of the rows on the current page, for the "Showing
  // X-Y" label. Both 0 when the page is empty (no results).
  start: number;
  end: number;
};

// `extraPagesCount` is the number of fetched pages beyond page 0; the last
// loaded page sits at index === extraPagesCount. `endReached` is set once a
// forward fetch comes back short (< pageSize), meaning the last loaded page is
// the final one. Next is allowed either when a later page is already buffered,
// or when we're on a full last page that might have more behind it.
export const pageWindow = (
  pageIndex: number,
  currentLen: number,
  extraPagesCount: number,
  endReached: boolean,
  pageSize: number = PAGE_LIMIT,
): PageWindow => {
  const onLastLoaded = pageIndex >= extraPagesCount;
  const canPrev = pageIndex > 0;
  const canNext = !onLastLoaded || (currentLen === pageSize && !endReached);
  const start = currentLen === 0 ? 0 : pageIndex * pageSize + 1;
  const end = pageIndex * pageSize + currentLen;
  return { canPrev, canNext, start, end };
};
